import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Users, ArrowLeft, Plus, AlertCircle, Search, X } from 'lucide-react';
import { api, type Business, type Job } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { NotificationBell } from '@/components/NotificationBell';
import { CommandPalette } from '@/components/crm/CommandPalette';
import { CrmNav } from '@/components/crm/CrmNav';
import { Avatar } from '@/components/crm/Avatar';
import { CRM_OPEN_PALETTE_EVENT, showCrmHero, TRADES_ONLY_SEGMENTS } from './crmNavConfig';
import { isRestaurantVertical } from '@/lib/vertical';
import {
  CRM_ATTENTION_REFRESH_EVENT,
  dismissAttentionJobs,
  filterAttentionJobs,
  pruneDismissedAttentionIds,
} from '@/lib/crmAttention';

export interface CrmOutletContext {
  business: Business;
  reloadBusiness: () => Promise<void>;
  reloadAttention: () => Promise<void>;
  openPalette: () => void;
}

export default function CrmLayout() {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [active, setActive] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [attentionJobs, setAttentionJobs] = useState<Job[]>([]);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const hero = businessId ? showCrmHero(pathname, businessId, active?.vertical) : false;
  const isRestaurant = isRestaurantVertical(active?.vertical);
  const segment = pathname.split('/').slice(3)[0] || 'overview';

  const reloadBusiness = async () => {
    if (!businessId) return;
    const { business } = await api.getBusiness(businessId);
    setActive(business);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { businesses: list } = await api.listBusinesses();
        if (cancelled) return;
        setBusinesses(list);
        const b = businessId ? list.find(x => x.id === businessId) : (list.find(x => x.status === 'live') || list[0]);
        if (b) {
          if (businessId !== b.id) navigate(`/crm/${b.id}/overview`, { replace: true });
          else setActive(b);
        } else {
          setActive(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businessId, navigate]);

  const reloadAttention = useCallback(async () => {
    if (!active) return;
    try {
      const { jobs } = await api.crm.failedDispatchJobs(active.id);
      pruneDismissedAttentionIds(
        active.id,
        jobs.map(j => j.id),
      );
      setAttentionJobs(filterAttentionJobs(jobs, active.id));
    } catch { /* */ }
  }, [active]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    async function load() {
      if (cancelled) return;
      await reloadAttention();
    }
    void load();
    const onRefresh = () => { void load(); };
    window.addEventListener(CRM_ATTENTION_REFRESH_EVENT, onRefresh);
    const t = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
      window.removeEventListener(CRM_ATTENTION_REFRESH_EVENT, onRefresh);
    };
  }, [active, reloadAttention]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(o => !o);
      }
    }
    function onPalette() {
      setPaletteOpen(true);
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener(CRM_OPEN_PALETTE_EVENT, onPalette);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(CRM_OPEN_PALETTE_EVENT, onPalette);
    };
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="hidden h-44 animate-pulse rounded-3xl bg-zinc-900/50 md:block" />
        <div className="h-10 w-full max-w-xl animate-pulse rounded-lg bg-zinc-900/50" />
      </div>
    );
  }

  if (businesses.length === 0) {
    return (
      <div className="grid place-items-center py-20 text-center">
        <div className="rounded-full bg-cyan-500/10 p-3 ring-4 ring-cyan-500/20">
          <Users className="size-7 text-cyan-300" />
        </div>
        <h2 className="mt-5 text-2xl font-semibold">No businesses yet</h2>
        <p className="mt-1 text-zinc-400">Set up your first AI receptionist to start collecting CRM data.</p>
        <div className="mt-6">
          <Link to="/onboarding/new"><Button size="lg">Start onboarding</Button></Link>
        </div>
      </div>
    );
  }

  if (!active) return <Navigate to="/dashboard" replace />;

  if (isRestaurant && TRADES_ONLY_SEGMENTS.has(segment)) {
    return <Navigate to={`/crm/${businessId}/overview`} replace />;
  }

  const ctx: CrmOutletContext = {
    business: active,
    reloadBusiness,
    reloadAttention,
    openPalette: () => setPaletteOpen(true),
  };

  function dismissAttentionBanner() {
    if (!active || attentionJobs.length === 0) return;
    dismissAttentionJobs(
      active.id,
      attentionJobs.map(j => j.id),
    );
    setAttentionJobs([]);
  }

  return (
    <div className="space-y-3 md:space-y-5">
      {hero ? (
        <div className="hidden md:block">
          <CrmHeroBanner business={active} businesses={businesses} onSearch={() => setPaletteOpen(true)} />
        </div>
      ) : (
        <div className="hidden md:block">
          <CrmCompactBar business={active} businesses={businesses} onSearch={() => setPaletteOpen(true)} />
        </div>
      )}

      {attentionJobs.length > 0 && !isRestaurant && (
        <div className="relative rounded-xl border border-rose-500/40 bg-rose-500/10 md:rounded-2xl">
          <button
            type="button"
            onClick={dismissAttentionBanner}
            className="absolute right-2 top-2 z-10 grid size-7 place-items-center rounded-lg text-rose-200/80 transition hover:bg-rose-500/20 hover:text-rose-50"
            aria-label="Dismiss alert"
          >
            <X className="size-4" />
          </button>
          <Link
            to={`/crm/${active.id}/pipeline?filter=failed-dispatch`}
            className="block px-3 py-2 pr-10 text-xs transition hover:bg-rose-500/15 md:px-4 md:py-3 md:text-sm"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-rose-300" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-rose-100">
                  {attentionJobs.length}{' '}
                  {attentionJobs.length === 1 ? 'new lead needs' : 'new leads need'} your attention
                </div>
                <p className="mt-0.5 text-[11px] text-rose-200/80 sm:text-xs">
                  Still in New leads with a dispatch issue — move them in Pipeline or open the list.
                </p>
              </div>
              <span className="hidden shrink-0 whitespace-nowrap rounded-full bg-rose-500/30 px-2 py-0.5 text-xs font-medium text-rose-100 sm:inline">
                Open →
              </span>
            </div>
          </Link>
        </div>
      )}

      <CrmNav businessId={active.id} vertical={active.vertical} />

      <div className="animate-fade-in min-w-0 max-w-full overflow-x-clip">
        <Outlet context={ctx} />
      </div>

      <CommandPalette businessId={active.id} open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}


function WorkspaceActions({
  business,
  businesses,
  onSearch,
}: {
  business: Business;
  businesses: Business[];
  onSearch: () => void;
}) {
  const navigate = useNavigate();

  return (
    <>
      <button
        type="button"
        onClick={onSearch}
        className="inline-flex h-9 items-center gap-2 rounded-xl border border-white/10 bg-zinc-900/60 px-3 text-sm text-zinc-300 backdrop-blur transition hover:bg-zinc-900 hover:text-white"
      >
        <Search className="size-4" />
        <span className="hidden sm:inline">Search…</span>
        <kbd className="hidden rounded border border-zinc-700 bg-zinc-950/60 px-1.5 py-0.5 text-[10px] font-mono text-zinc-400 sm:inline">⌘K</kbd>
      </button>
      {businesses.length > 1 && (
        <select
          value={business.id}
          onChange={e => navigate(`/crm/${e.target.value}/overview`)}
          className="h-9 max-w-[10rem] truncate rounded-xl border border-white/10 bg-zinc-900/60 px-3 text-sm text-zinc-200 backdrop-blur sm:max-w-none"
        >
          {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      )}
      <NotificationBell businessId={business.id} />
      <Link to={`/crm/${business.id}/customers?new=1`}>
        <Button variant="secondary" size="sm" className="!border-white/10 !bg-zinc-900/60 backdrop-blur">
          <Plus className="size-4" />
          <span className="hidden sm:inline">Add contact</span>
        </Button>
      </Link>
    </>
  );
}

function CrmHeroBanner({
  business,
  businesses,
  onSearch,
}: {
  business: Business;
  businesses: Business[];
  onSearch: () => void;
}) {
  return (
    <section className="relative overflow-hidden rounded-3xl border border-zinc-800/80 shadow-xl shadow-black/30">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(/crm/img/overview.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(120deg, rgba(9,9,11,0.92) 0%, rgba(9,9,11,0.7) 50%, rgba(9,9,11,0.55) 100%), ' +
            'radial-gradient(900px 320px at 90% -20%, rgba(34,211,238,0.18), transparent 60%), ' +
            'radial-gradient(700px 320px at 0% 120%, rgba(168,85,247,0.18), transparent 60%)',
        }}
      />
      <div className="relative flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:gap-4 sm:px-8 sm:py-6">
        <div className="flex min-w-0 items-center gap-4">
          <Link
            to={`/dashboard/${business.id}`}
            className="grid size-10 place-items-center rounded-xl border border-white/10 bg-zinc-900/60 text-zinc-300 backdrop-blur transition hover:bg-zinc-900 hover:text-white"
            title="Back to dashboard"
          >
            <ArrowLeft className="size-4" />
          </Link>
          <Avatar seed={business.id} name={business.name} size={44} square />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-300/80">CRM Workspace</p>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-2xl font-semibold tracking-tight text-white sm:text-3xl">{business.name}</h1>
              <Badge tone={business.status === 'live' ? 'live' : 'warning'}>{business.status}</Badge>
            </div>
            {business.industry && (
              <p className="mt-0.5 truncate text-xs text-zinc-300/80">{business.industry}</p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <WorkspaceActions business={business} businesses={businesses} onSearch={onSearch} />
        </div>
      </div>
    </section>
  );
}

function CrmCompactBar({
  business,
  businesses,
  onSearch,
}: {
  business: Business;
  businesses: Business[];
  onSearch: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-zinc-800/80 bg-zinc-900/40 px-4 py-3 backdrop-blur">
      <div className="flex min-w-0 items-center gap-3">
        <Link
          to={`/dashboard/${business.id}`}
          className="grid size-9 shrink-0 place-items-center rounded-lg border border-zinc-800 bg-zinc-950/60 text-zinc-400 transition hover:text-white"
          title="Back to dashboard"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <Avatar seed={business.id} name={business.name} size={36} square />
        <div className="min-w-0">
          <h1 className="truncate text-base font-semibold text-white sm:text-lg">{business.name}</h1>
          <Link
            to={`/crm/${business.id}/overview`}
            className="text-xs text-cyan-400/90 hover:text-cyan-300"
          >
            ← Overview
          </Link>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <WorkspaceActions business={business} businesses={businesses} onSearch={onSearch} />
      </div>
    </div>
  );
}
